import { Box } from '@mui/material';
import { useTheme , createTheme } from '@mui/material/styles';
import { symbol, symbolTriangle } from 'd3';
import React, { memo, useMemo, useState } from 'react';
import { Bar, BarChart, Customized, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import strings from "../../Globalization/index";
import consts from '../../consts';

const emptyBarValidation = [null, undefined, consts.channelPointerLocationValue.notFlushed.value, consts.LeakTest.ConnectionError, consts.channelPointerLocationValue.inActive.value];
const themeColor = createTheme({
	palette: {
		divider: 'rgba(0, 0, 0, 0.5)', // Make it darker than the default (usually around 0.12 opacity)
	},
});

const TiangleValueIndicator = (props) => {
	const { data, width, fill, stroke, size = 64, strokeWidth = 1 } = props;
	const indicatorXPos = (width * data[0]?.indicator)
	const [scale, setScale] = useState(1);

	return (
		<path
			d={symbol().type(symbolTriangle).size(size)()}
			onMouseEnter={() => setScale(1.2)}
			onMouseLeave={() => setScale(1)}
			fill={fill || '#FFFFFF'}
			transform={`translate(${indicatorXPos}, 7.5) rotate(180) scale(${scale})`}
			stroke={stroke || '#000000'}
			strokeWidth={strokeWidth}
			style={{
				transition: 'all 0.15s 0.15s'
			}}
		/>
	);
}

const internalValueMapper = {
	ISA: {
		0: consts.channelPointerLocationValue.inActive.barPointerValue,
		1: consts.channelPointerLocationValue.pass.barPointerValue,
		2: consts.channelPointerLocationValue.blocked.barPointerValue,
		3: consts.channelPointerLocationValue.disconnected.barPointerValue,
		4: consts.channelPointerLocationValue.notFlushed.barPointerValue
	},
	default: {
		1: consts.LeakTest.Pass,
		2: consts.LeakTest.Pass,
		3: consts.LeakTest.Blocked,
		4: consts.LeakTest.Disconnected,
		5: consts.LeakTest.ConnectionError
	}
}
const firstLabelMapper = {
	ISA: {
		[consts.channelPointerLocationValue.inActive.value]: "InActive",
		[consts.channelPointerLocationValue.notFlushed.value]: "notFlushed"
	},
	default: {
		[consts.LeakTest.ConnectionError]: "ConnectionError",
	}
}

const LinearGaugeChart = ({ width = 250, height = 48, title = 'Title', value, indicatorFill, indicatorStroke, IsISA = false, mapperName = 'default' }) => {
	const theme = useTheme();

	const internalValue = internalValueMapper[mapperName][value];
	let barFillColor = themeColor.palette.divider;
	let labelFillColor = theme.palette.text.primary;
	if ((value > 0 && !IsISA) || (IsISA && ![0, 4, null, undefined].includes(value))) {
		barFillColor = theme.palette.error.light;
		labelFillColor = theme.palette.background.paper;
	}
	const [data] = useMemo(() => {
		const dataArr = [];
		const isNeedtoShowEmptyBar = emptyBarValidation.includes(internalValue);
		if (isNeedtoShowEmptyBar) {
			const firstLabel = firstLabelMapper[mapperName][value] || consts.empty;
			dataArr.push({
				first: 5,
				second: 0,
				third: 0,
				firstLabel: strings[firstLabel],
				secondLabel: '',
				thirdLabel: '',
				title: title,
				value: internalValue,
				indicator: (internalValue || 0) / 5
			})
		} else {
			dataArr.push({
				first: 1.67,
				second: 1.67,
				third: 1.67,
				firstLabel: strings.Blocked,
				secondLabel: (value === 2 && !IsISA) ? strings.Pass_W_Cond : strings.Pass,
				thirdLabel: strings.Disconnected,
				title: title,
				value: internalValue,
				indicator: internalValue / (1.67 * 3)
			})
		}

		return [dataArr];
	}, [internalValue, value]);


	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				width, // match chart width
				'& svg': {
					'&.recharts-surface': { overflow: 'visible' }
				}
			}}
		>
			<ResponsiveContainer height={height} width={width}>
				<BarChart
					data={data}
					layout={'vertical'}
					barGap={10}
					margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
					sv
				>
					<XAxis type={'number'} hide domain={[0, 5]} />
					<YAxis type={'category'} axisLine={false} tickLine={false} hide />
					<Bar
						dataKey="first"
						stackId="b"
						fill={barFillColor}
						isAnimationActive={false}
					>
						<LabelList
							dataKey="firstLabel"
							position="center"
							style={{ fill: labelFillColor }}
							fontSize={11}
						/>
					</Bar>
					<Bar dataKey="second" stackId="b" fill={theme.palette.success.light} isAnimationActive={false}>
						<LabelList
							dataKey="secondLabel"
							position="center"
							style={{ fill: theme.palette.background.paper }}
							fontSize={11}
						/>
					</Bar>
					<Bar dataKey="third" stackId="b" fill={theme.palette.error.light} isAnimationActive={false}>
						<LabelList
							dataKey="thirdLabel"
							position="center"
							style={{ fill: theme.palette.background.paper }}
							fontSize={11}
						/>
					</Bar>
					<Customized
						component={(props) => (
							<TiangleValueIndicator
								{...props}
								fill={indicatorFill || theme.palette.background.paper}
								stroke={indicatorStroke || theme.palette.text.primary}
								data={data}
								width={width}
							/>
						)}
						isFront
					/>
				</BarChart>
			</ResponsiveContainer>
		</Box>
	);
};

export default memo(LinearGaugeChart);